import type { RequestContext } from "../context/request-context.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../domain/identity/access-types.js";
import type { DocumentStorageService } from "./document-storage-service.js";
import {
  isEmbeddableImageDataUrl,
  normalizeAnnotation,
  restoreOriginal as restoreOriginalImage,
  AnnotationRuleError,
  type AnnotatedImage,
  type ImageAnnotation
} from "../../domain/imaging/image-annotation.js";

/**
 * EDITOR DE IMAGEM — persistência da imagem original e da versão anotada.
 *
 * A regra que sustenta o módulo inteiro (comando, itens 3/22/40): a imagem
 * ORIGINAL nunca é sobrescrita. Anotar gera SEMPRE um novo blob, com
 * `storageRef` próprio, e o registro passa a apontar para ele — a original
 * continua acessível pelo `originalFileId`.
 *
 * Isso não é preciosismo de versionamento: um PDF histórico regerado relê a
 * foto do cadastro. Se a edição substituísse o binário original, um relatório
 * assinado meses atrás passaria a sair com marcações que não existiam quando
 * foi assinado. Gravando em um blob NOVO, o documento histórico que aponta
 * para o blob antigo continua idêntico.
 *
 * O binário trafega e é guardado como data URL (`data:image/png;base64,...`),
 * que é o formato aceito pelo `DocumentStorageService` e o único que os
 * builders de PDF (pdfmake) conseguem embutir.
 */

/**
 * Permissões usadas pelo editor. Cada módulo informa as suas (comando, item
 * 31: "conforme regra do módulo") — o editor não cria permissão nova nem
 * inventa bypass. O padrão serve aos módulos documentais.
 */
export interface ImageAnnotationPermissions {
  readonly view: string;
  readonly edit: string;
}

const DEFAULT_PERMISSIONS: ImageAnnotationPermissions = {
  view: PermissionCode.DocumentRead,
  edit: PermissionCode.DocumentWrite
};

export interface SaveAnnotationInput {
  readonly image: AnnotatedImage;
  readonly annotations: readonly ImageAnnotation[];
  /**
   * Imagem achatada (original + marcações) renderizada pelo cliente, em data
   * URL. É subproduto: a fonte editável continua sendo `annotations`.
   */
  readonly renderedDataUrl: string;
  /** Contexto de auditoria: qual documento/registro usa esta imagem. */
  readonly contextLabel?: string;
}

export class ImageAnnotationService {
  private readonly auth = new RbacAuthorizationService();

  constructor(
    private readonly storage: DocumentStorageService,
    private readonly audit?: AuditRecorder,
    private readonly permissions: ImageAnnotationPermissions = DEFAULT_PERMISSIONS
  ) {}

  /** Guarda a imagem recém-anexada e devolve o registro sem anotação alguma. */
  public async storeOriginal(context: RequestContext, dataUrl: string): Promise<AnnotatedImage> {
    this.auth.require(context, this.permissions.edit as never);
    this.requireImageDataUrl(dataUrl);
    const originalFileId = await this.storage.store(dataUrl);
    return { originalFileId, annotatedFileId: null, annotations: [], updatedBy: null, updatedAt: null };
  }

  /**
   * SALVAR EDIÇÃO (item 16): valida as marcações, grava a imagem achatada
   * como um blob NOVO e devolve o registro apontando para ela. A anterior
   * não é removida — um snapshot de revisão fechada pode estar usando.
   */
  public async saveAnnotation(context: RequestContext, input: SaveAnnotationInput): Promise<AnnotatedImage> {
    this.auth.require(context, this.permissions.edit as never);
    if (!input.image?.originalFileId) {
      throw new AppError(ErrorCode.Validation, "Original required", { safeMessage: "A imagem original não foi encontrada para edição." });
    }
    this.requireImageDataUrl(input.renderedDataUrl);

    let annotations: ImageAnnotation[];
    try {
      annotations = input.annotations.map(annotation => normalizeAnnotation(annotation));
    } catch (error) {
      if (error instanceof AnnotationRuleError) {
        throw new AppError(ErrorCode.Validation, "Annotation rule", { safeMessage: error.message });
      }
      throw error;
    }

    if (annotations.length === 0) {
      // Salvar sem nenhuma marcação equivale a voltar para a original — não
      // faz sentido criar um blob idêntico à foto sem nada desenhado.
      return this.restoreOriginal(context, input.image, input.contextLabel);
    }

    const previousAnnotatedFileId = input.image.annotatedFileId;
    const annotatedFileId = await this.storage.store(input.renderedDataUrl);
    const saved: AnnotatedImage = {
      originalFileId: input.image.originalFileId,
      annotatedFileId,
      annotations,
      updatedBy: context.userId ? String(context.userId) : null,
      updatedAt: new Date().toISOString()
    };

    await this.record(context, AuditAction.ImageAnnotationSaved, saved.originalFileId, {
      contextLabel: input.contextLabel ?? null,
      previousAnnotatedFileId,
      newAnnotatedFileId: annotatedFileId,
      annotationCount: annotations.length,
      annotationTypes: annotations.map(annotation => annotation.type)
    });
    return saved;
  }

  /** RESTAURAR ORIGINAL (item 20) — volta a usar a original, sem apagar o blob anotado. */
  public async restoreOriginal(context: RequestContext, image: AnnotatedImage, contextLabel?: string): Promise<AnnotatedImage> {
    this.auth.require(context, this.permissions.edit as never);
    const restored: AnnotatedImage = {
      ...restoreOriginalImage(image),
      updatedBy: context.userId ? String(context.userId) : null,
      updatedAt: new Date().toISOString()
    };
    await this.record(context, AuditAction.ImageAnnotationRestored, image.originalFileId, {
      contextLabel: contextLabel ?? null,
      discardedAnnotatedFileId: image.annotatedFileId
    });
    return restored;
  }

  /**
   * Resolve o binário para exibição/PDF. Recebe o `storageRef` já escolhido
   * pelo chamador (`effectiveFileIdOf`), não decide sozinho qual versão usar.
   */
  public async resolveDataUrl(context: RequestContext, storageRef: string): Promise<string | null> {
    this.auth.require(context, this.permissions.view as never);
    if (!storageRef) return null;
    return this.storage.resolveDownloadUrl(storageRef);
  }

  private requireImageDataUrl(dataUrl: string): void {
    if (!isEmbeddableImageDataUrl(dataUrl)) {
      throw new AppError(ErrorCode.Validation, "Invalid image", {
        safeMessage: "Formato de imagem não suportado. Use PNG, JPG ou WEBP."
      });
    }
  }

  private async record(context: RequestContext, action: AuditAction, entityId: string, metadata: Record<string, unknown>): Promise<void> {
    if (!this.audit) return;
    try {
      await this.audit.record(context, {
        action,
        entityType: "IMAGE_ANNOTATION",
        entityId,
        previousState: null,
        newState: null,
        metadata: metadata as never
      });
    } catch (error) {
      console.warn("[EDITOR DE IMAGEM] Falha ao registrar auditoria", error);
    }
  }
}
